/**
 * SEC-PORTAL-2 — which Supabase client a CLIENT-PORTAL read goes through.
 *
 * SEC-PORTAL-1 gave the eleven portal read policies `TO anon`, which is
 * what stopped every signed-in Orbit user from seeing every public
 * gallery in the database. It also broke the honest case: a client who
 * happens to have their own Orbit account, opening their photographer's
 * delivery link, arrives as `authenticated`, not `anon`, so the policies
 * refuse them and the gallery renders EMPTY.
 *
 * The fix is a second client with no session. The interesting part is
 * deciding WHEN to use it, because which client is right is not a
 * property of the person — it is a property of the SURFACE. These hooks
 * are shared: `useGalleryPicks`, `useGalleryComments` and
 * `useGalleryEngine` draw the client's delivery link AND the studio's
 * own gallery tab. A studio reading its own unpublished gallery must
 * still read as itself.
 *
 * So it is derived from the route, at call time, rather than held in a
 * flag. A flag has to be set before the first read and cleared on every
 * exit; a derivation is simply never stale, and it follows a client-side
 * navigation for free.
 *
 * THE RULE for this list: a prefix belongs here only if EVERY route
 * under it is client-facing. `/gallery/` qualifies — the studio's own
 * gallery screens are `/galleries/` (plural) and `/gallery-editor`.
 */
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { supabase } from './client';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

/**
 * The same project, signed in as nobody.
 *
 * `persistSession: false` is the whole contract — supabase-js then never
 * reads the stored session, so the request goes out with the publishable
 * key alone and PostgREST runs it as `anon`. The distinct `storageKey`
 * is only to keep it out of the other client's namespace (and to stop
 * the "Multiple GoTrueClient instances" warning).
 */
export const portalSupabase = createClient<Database>(
  SUPABASE_URL,
  SUPABASE_PUBLISHABLE_KEY,
  {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
      storageKey: 'orbit-portal-no-session',
    },
  },
);

const PORTAL_PREFIXES = [
  '/portal/',
  '/delivery/',
  '/gallery/',
  '/video/',
  '/inquiry-portal/',
  '/d/',
  '/s/',
];

/** Is the page being shown right now a client-facing one? */
export function isPortalRoute(
  pathname: string = typeof window === 'undefined' ? '' : window.location.pathname,
): boolean {
  return PORTAL_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/**
 * The client a portal READ should go through: session-less on a
 * client-facing route, the signed-in one everywhere else.
 *
 * Writes are deliberately not routed through this. Every portal write is
 * a SECURITY DEFINER RPC that already runs for a visitor with no account
 * at all, so it works from either client, and leaving them alone keeps
 * this change to the reads that are actually broken. Studio writes
 * (`.update()` on galleries and comments) must keep their session.
 */
export function portalReads() {
  return isPortalRoute() ? portalSupabase : supabase;
}
